import type { VuiComponent } from '../core'
import type { NotificationProps } from './notification.types'

import Box from '../box'
import { styled, useStyleConfig, vui } from '../core'
import { DismissButton } from '../dismissButton'
import shadows from '../theme/foundations/shadows'
import { cs, filterUndefined, isReactText, isString } from '../utils'
import { INTENT_ICON_MAP, notificationStatusMapping, LOADING_ICON_PROPS } from './consts'
import { NotificationProvider } from './context'
import NotificationAction from './notificationAction'
import NotificationIcon from './notificationIcon'
import NotificationText from './notificationText'
import NotificationTitle from './notificationTitle'
import { DEFAULT_VARIANT, inferNotificationLayout, resolveNotificationAria, resolveNotificationVariant } from './utils'

const linkSwipeAnimation = `
  @keyframes linkSwipe {
    from {
      background-size: 0 1px;
    }
    to {
      background-size: 100% 1px;
    }
  }
`

const NotificationContainer = styled(Box)<{ $dismissFg?: string; $isBanner?: boolean }>`
  ${({ $dismissFg }) => $dismissFg && `--vui-dismiss-fg: ${$dismissFg};`}
  ${linkSwipeAnimation}
  .vui-notificationText a {
    text-decoration: none;
    background: linear-gradient(to right, currentColor, currentColor);
    background-size: 100% 1px;
    background-position: 0 100%;
    background-repeat: no-repeat;
    padding-bottom: 2px;
  }
  .vui-notificationText a:hover {
    animation: linkSwipe 0.5s ease forwards;
    background-size: 100% 1px;
  }
  ${({ $isBanner }) =>
    $isBanner &&
    `
    box-shadow: ${shadows.md};
    position: relative;
    width: 100%;
    border: none;
  `}
`

/**
 * Displays informational content, like text and icon with optional action buttons.
 * Handles multiple states, like info, error or loading.
 */
export const Notification = vui<'div', NotificationProps>((props, ref) => {
  const {
    action,
    align,
    ariaLive,
    children,
    className,
    icon,
    intent,
    isLoading,
    onClose,
    role,
    showDismissButton,
    size,
    status,
    statusMapping,
    text,
    title,
    variant: variantProp = DEFAULT_VARIANT,
    verticalAlign,
    ...rest
  } = props

  const statuses = { ...notificationStatusMapping, ...statusMapping }

  // Derive variant/intent/isLoading from deprecated status prop when not explicitly set.
  // Explicit props always win; status is a convenience shorthand only.
  const statusProps = status ? statuses[status] : undefined
  const resolvedVariant = variantProp !== DEFAULT_VARIANT ? variantProp : (statusProps?.variant ?? variantProp)
  // Q9: when isLoading is true, default intent to 'info' if not specified.
  const intentFromProps = intent ?? statusProps?.intent
  const resolvedIsLoading = isLoading ?? statusProps?.isLoading
  const resolvedIntent = resolvedIsLoading && intentFromProps == null ? 'info' : intentFromProps

  const { themeKey, isBanner } = resolveNotificationVariant(resolvedVariant, resolvedIntent)

  // Q2: infer layout from content composition; no `layout` prop.
  const layout = inferNotificationLayout({ action, text, title, hasStatus: !!statusProps })

  // Q4: auto-map role/aria-live from intent (and isLoading). Explicit props win.
  const aria = resolveNotificationAria({ intent: resolvedIntent, isLoading: resolvedIsLoading })
  const resolvedRole = role ?? aria.role
  const resolvedAriaLive = ariaLive ?? aria.ariaLive

  const styles = useStyleConfig('Notification', { variant: themeKey, size })

  // Auto-inject icon only when using the default render path (no custom children).
  // When children are provided, the consumer manages icons via <Notification.Icon>.
  // An explicit string `icon` prop always wins regardless of children.
  // Priority: explicit icon → loading → status mapping → intent mapping
  const autoIconProps = resolvedIsLoading
    ? LOADING_ICON_PROPS
    : (statuses[status ?? '']?.iconProps ?? (resolvedIntent ? INTENT_ICON_MAP[resolvedIntent] : undefined))

  const showIcon = icon !== ''
  const iconName = showIcon ? (isString(icon) ? icon : (!children && autoIconProps?.name) || undefined) : undefined

  const context = filterUndefined<NotificationProps>({
    size,
    status,
    statusMapping,
    variant: themeKey,
    intent: resolvedIntent,
    isLoading: resolvedIsLoading,
  })
  // Q10: expose the inferred layout to Notification.Action via context.
  // Use a non-prop key so the context payload doesn't pollute NotificationProps consumers.
  ;(context as Record<string, unknown>).layout = layout

  // verticalAlign is deprecated; map old values to the new align prop
  const resolvedAlign =
    align ?? (verticalAlign === 'center' ? 'center' : verticalAlign === 'flex-start' ? 'top' : undefined)
  // Q11: align is the only manual override. When omitted, fall back to layout-driven default.
  const layoutDefaultAlign = layout === 'one-line' ? 'center' : 'top'
  const alignSelf = (resolvedAlign ?? layoutDefaultAlign) === 'center' ? 'center' : 'flex-start'

  return (
    <NotificationProvider value={context}>
      <NotificationContainer
        borderRadius="md"
        borderWidth={1}
        className={cs('vui-notification', className)}
        ref={ref}
        role={resolvedRole}
        aria-live={resolvedAriaLive}
        data-intent={resolvedIntent}
        data-variant={isBanner ? 'banner' : 'inline'}
        data-layout={layout}
        data-loading={resolvedIsLoading ? 'true' : undefined}
        data-align={resolvedAlign}
        w="fit-content"
        $dismissFg={styles.container?.['--vui-dismiss-fg']}
        $isBanner={isBanner}
        {...styles.container}
        {...rest}
      >
        {iconName ? (
          <Box alignSelf={alignSelf} {...styles.icon}>
            <NotificationIcon {...autoIconProps} name={iconName} />
          </Box>
        ) : showIcon ? (
          icon
        ) : null}

        {children ?? (
          <Box column={alignSelf !== 'center'} flex={1} {...styles.content}>
            <NotificationText>
              {title ? (
                isReactText(title) ? (
                  <NotificationTitle
                    display={alignSelf !== 'center' ? 'block' : 'inline'}
                    mb={alignSelf !== 'center' && text ? styles.title?.mb : undefined}
                    text={title}
                  />
                ) : (
                  title
                )
              ) : null}
              {title && text && alignSelf === 'center' ? ' ' : null}
              {text}
            </NotificationText>
            {action ? <NotificationAction>{action}</NotificationAction> : null}
          </Box>
        )}

        {showDismissButton && (
          <DismissButton
            alignSelf={alignSelf}
            aria-label="Close"
            size="md"
            onClick={onClose || (() => {})}
            {...styles.button}
          />
        )}
      </NotificationContainer>
    </NotificationProvider>
  )
}) as VuiComponent<'div', NotificationProps> & {
  Action: typeof NotificationAction
  Icon: typeof NotificationIcon
  Text: typeof NotificationText
  Title: typeof NotificationTitle
}

Notification.Action = NotificationAction
Notification.Icon = NotificationIcon
Notification.Text = NotificationText
Notification.Title = NotificationTitle
Notification.displayName = 'Notification'

export default Notification
